import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type HelpProps = {
  examType: string;
  questionsCount: number;
  minutes: number;
};

type TabId = "question" | "section" | "general" | "tools" | "screen";

const tabs: { id: TabId; label: string }[] = [
  { id: "question", label: "Question Directions" },
  { id: "section", label: "Section Directions" },
  { id: "general", label: "General Directions" },
  { id: "tools", label: "Testing Tools" },
  { id: "screen", label: "Screen Reader Help" },
];

function Help({ examType, questionsCount, minutes }: HelpProps) {
  const [activeTab, setActiveTab] = useState<TabId>("section");

  const sectionTitle =
    examType === "QUANT" ? "Quantitative Reasoning" : "Verbal Reasoning";

  const renderSectionDirections = () => (
    <div style={{ fontSize: "13px", lineHeight: 1.4, textAlign: "left" }}>
      <h3 className="mb-1" style={{ fontSize: "22px", fontWeight: 600 }}>
        {sectionTitle}
      </h3>
      <p className="mb-0" style={{ fontWeight: 600 }}>
        {questionsCount} Questions
      </p>
      <p className="mb-3" style={{ fontWeight: 600 }}>
        {minutes} minutes
      </p>
      <hr className="mt-2 mb-3" style={{ borderTop: "1px solid #bbb" }} />
      <p className="mb-2">
        For each question, indicate the best answer using the directions given.
        If you need more detailed directions, select the{" "}
        <b>Question Directions</b> tab above.
      </p>
      <p className="mb-2">
        If a question has answer choices with <b>ovals</b>, then the correct
        answer consists of a single choice. If a question has answer choices
        with <b>squares</b>, then the correct answer may consist of more than
        one choice.
      </p>
      <p className="mb-0">
        Select <b>Continue</b> to proceed.
      </p>
    </div>
  );

  const renderQuestionDirections = () => (
    <div style={{ fontSize: "13px", lineHeight: 1.4, textAlign: "left" }}>
      <h3 className="mb-2" style={{ fontSize: "20px", fontWeight: 600 }}>
        Multiple Choice Question, Select One or More Answer Choices
      </h3>
      <p className="mb-1" style={{ fontWeight: 600 }}>
        Select one or more answer choices.
      </p>
      <p className="mb-1">
        To select an answer choice, click on it or select it with the keyboard.
        To deselect a choice, click on it or select it again.
      </p>
      <ul className="mb-3" style={{ paddingLeft: "18px" }}>
        <li>
          The correct answer may be just one of the choices or as many as all
          of the choices, depending on the question.
        </li>
        <li>
          No credit is given unless you select all of the correct choices and no
          others.
        </li>
      </ul>
      <p className="mb-2" style={{ fontWeight: 600 }}>
        Example:
      </p>
      <div
        className="mt-1"
        style={{
          border: "1px solid #000",
          display: "flex",
          alignItems: "stretch",
          fontSize: "12px",
        }}
      >
        <div
          style={{
            flex: 1,
            borderRight: "1px solid #000",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              backgroundColor: "#1f2b4d",
              color: "#fff",
              padding: "4px 8px",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            Question 1 is based on this passage.
          </div>
          <div style={{ padding: "8px", lineHeight: 1.5 }}>
            Reviving the practice of using elements of popular music in
            classical composition, an approach that had been in hibernation in
            the United States during the 1960s, composer Philip Glass embraced
            the ethos of popular music without imitating it. Glass based two
            symphonies on music by rock musicians, but the symphonies&apos;
            sound is distinctively his. Popular elements do not appear out of
            place in Glass&apos;s classical music, which from its early days has
            shared certain harmonies and rhythms with rock music.
          </div>
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              backgroundColor: "#e0e0e0",
              padding: "6px 8px",
              borderBottom: "1px solid #000",
              fontWeight: 600,
            }}
          >
            Consider each of the choices separately and select all that apply.
          </div>
          <div style={{ padding: "8px", lineHeight: 1.5 }}>
            <p className="mb-2">
              The passage suggests that Glass&apos;s work displays which of the
              following qualities?
            </p>
            <p className="mb-1">
              ☒ A return to the use of popular music in classical compositions.
            </p>
            <p className="mb-1">
              ☐ An attempt to elevate rock music to an artistic status more
              closely approximating that of classical music.
            </p>
            <p className="mb-1">
              ☒ A long-standing tendency to incorporate elements from two
              apparently disparate musical styles.
            </p>
          </div>
          <div
            style={{
              marginTop: "auto",
              backgroundColor: "#e0e0e0",
              padding: "6px 8px",
              borderTop: "1px solid #000",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Select one or more answer choices.
          </div>
        </div>
      </div>
    </div>
  );

  const renderGeneralDirections = () => (
    <div style={{ fontSize: "13px", lineHeight: 1.4, textAlign: "left" }}>
      <h3 className="mb-2" style={{ fontSize: "20px", fontWeight: 600 }}>
        General Test Information
      </h3>
      <p className="mb-1" style={{ fontWeight: 600 }}>
        Timing Information
      </p>
      <p className="mb-2">
        Total testing time on this test is {Math.round(minutes * 4)} minutes
        (standard time).
      </p>
      <p className="mb-1" style={{ fontWeight: 600 }}>
        Test Information
      </p>
      <p className="mb-1">
        Within a timed section you may skip any question and return to it later
        before you exit the section. You should answer as many questions as
        possible during the test and manage your time with this in mind.
      </p>
      <p className="mb-1">
        Use the <b>Review</b> button at any time during the test to review
        which questions you have answered and which questions you have skipped.
      </p>
      <p className="mb-0">
        Select <b>Continue</b> to proceed.
      </p>
    </div>
  );

  const renderTestingTools = () => (
    <div style={{ fontSize: "13px", lineHeight: 1.4, textAlign: "left" }}>
      <h3 className="mb-3" style={{ fontSize: "20px", fontWeight: 600 }}>
        Testing Tools
      </h3>

      {/* Next */}
      <hr className="mt-0 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#3A669C",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Next
          <FontAwesomeIcon icon="step-forward" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          After you answer a question, select <b>Next</b>. You may still return
          to the question and change your answer after selecting <b>Next</b>.
        </div>
      </div>

      {/* Back */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#3A669C",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Back
          <FontAwesomeIcon icon="step-backward" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          Selecting <b>Back</b> will take you to the previous screen or
          question.
        </div>
      </div>

      {/* Help */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#575658",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Help
          <FontAwesomeIcon icon="question-circle" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          By selecting <b>Help</b> you can get information on different topics.
          The topics are presented on different tabs. You are now in{" "}
          <b>Help</b>.
        </div>
      </div>

      {/* Review */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#575658",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Review
          <FontAwesomeIcon icon="sitemap" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          Selecting <b>Review</b> will allow you to access the{" "}
          <b>Review</b> screen. The Review screen lists all of the questions in
          the section and their status.
        </div>
      </div>

      {/* Mark */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#575658",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Mark
          <FontAwesomeIcon icon="bookmark" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          Selecting <b>Mark</b> will place a check mark in the Review screen
          next to a question you may want to review again. Selecting{" "}
          <b>Mark</b> again will remove the check mark.
        </div>
      </div>

      {/* Info */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#575658",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Info
          <FontAwesomeIcon icon="info-circle" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          Selecting <b>Info</b> displays additional information about the exam
          section you are taking.
        </div>
      </div>

      {/* Calc */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#575658",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Calc
          <FontAwesomeIcon icon="calculator" className="d-block mx-auto" />
        </button>
        <div className="pe-5 pt-3">
          Selecting <b>Calc</b> opens the on-screen calculator, when it is
          available for the current section.
        </div>
      </div>

      {/* Exit */}
      <hr className="mt-2 mb-2" style={{ borderTop: "1px solid #ddd" }} />
      <div className="mb-2 d-flex align-items-start">
        <button
          type="button"
          className="btn py-2 px-3 text-center fw-light border border-secondary"
          style={{
            backgroundColor: "#694C61",
            color: "#fff",
            borderRadius: 0,
            minWidth: "90px",
            cursor: "default",
          }}
        >
          Quit w/Save
          <FontAwesomeIcon
            icon="save"
            className="d-block mx-auto"
          />
        </button>
        <div className="pe-5 pt-3">
          Selecting <b>Quit w/Save</b> will leave the exam screen. Once you confirm
          exit, you may not be able to return to the current section.
        </div>
      </div>
    </div>
  );

  const renderScreenReaderHelp = () => (
    <div style={{ fontSize: "13px", lineHeight: 1.4, textAlign: "left" }}>
      <h3 className="mb-2" style={{ fontSize: "20px", fontWeight: 600 }}>
        Help for JAWS and Braille Users
      </h3>
      <p className="mb-2">
        This section summarizes how the test is structured and how to move
        around it using a screen reader and keyboard. The notes assume the
        standard JAWS configuration, but the ideas are similar for other
        readers.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        About This Test
      </p>
      <p className="mb-2">
        The test content is split into labeled regions. Regions let you jump to
        different parts of the page, such as the question area, the answer
        choices, or the navigation controls.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Activating Controls
      </p>
      <ul className="mb-2" style={{ paddingLeft: "18px" }}>
        <li>Press Enter or Spacebar to activate a button.</li>
        <li>Press Enter or Spacebar to check or uncheck a check box.</li>
        <li>
          Use Up Arrow and Down Arrow to move among items in combo boxes and
          lists.
        </li>
      </ul>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Announcement Toggle Features
      </p>
      <p className="mb-1">
        Some passages may contain optional announcements, such as audio cues or
        highlighted text. When these features are available, controls are
        provided so that you can turn them on or off without leaving the test
        screen.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Blank Areas
      </p>
      <p className="mb-2">
        If a region of the page includes only layout or spacing, JAWS may
        report it as a blank area. This is expected and does not indicate that
        any content is missing.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Forms Mode and Browse Mode
      </p>
      <ul className="mb-2" style={{ paddingLeft: "18px" }}>
        <li>
          In Browse mode, you can move through headings, paragraphs, and links
          using standard quick keys.
        </li>
        <li>
          When you reach a control that accepts input (for example a text box
          or check box), press Enter to switch to Forms mode.
        </li>
        <li>
          Press Escape or NumPad Plus (JAWS) to leave Forms mode and return to
          Browse mode.
        </li>
      </ul>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Editing Text
      </p>
      <p className="mb-1">
        In writing tasks or text boxes, standard editing keystrokes are
        available. Common examples include:
      </p>
      <ul className="mb-2" style={{ paddingLeft: "18px" }}>
        <li>Ctrl+Left Arrow / Ctrl+Right Arrow to move by word.</li>
        <li>Home / End to move to the start or end of a line.</li>
        <li>Ctrl+A to select all text in the current field.</li>
      </ul>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Navigation Quick Keys
      </p>
      <p className="mb-1">
        When Browse mode is active, you can use quick keys to move directly to
        specific elements:
      </p>
      <ul className="mb-2" style={{ paddingLeft: "18px" }}>
        <li>H / Shift+H: next or previous heading.</li>
        <li>T / Shift+T: next or previous table.</li>
        <li>B / Shift+B: next or previous button.</li>
        <li>K / Shift+K: next or previous link.</li>
      </ul>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Links
      </p>
      <p className="mb-2">
        Links in the test may open reference material or move you to a specific
        question. Use Tab and Shift+Tab to move through links, or use the
        navigation quick keys described above.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Radio Buttons and Check Boxes
      </p>
      <ul className="mb-2" style={{ paddingLeft: "18px" }}>
        <li>
          Use Up Arrow and Down Arrow to move among answer choices in a radio
          button group.
        </li>
        <li>
          Press Spacebar to select or clear the current choice. For radio
          buttons, only one choice can be selected at a time.
        </li>
      </ul>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Reading the Passage and Question
      </p>
      <p className="mb-2">
        In reading sections, the passage and its related question may appear in
        separate regions. Use heading navigation or region navigation to move
        between the passage, the question stem, and the answer choices.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Regions
      </p>
      <p className="mb-1">
        Regions divide the screen into logical blocks such as &quot;Question
        content&quot;, &quot;Answer choices&quot;, or &quot;Navigation
        controls&quot;. Moving by region lets you skip repeated information and
        go straight to the area you need.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Tab Lists
      </p>
      <p className="mb-2">
        Some screens present a row of tabs, like the tabs at the top of this
        Help page. Use Left Arrow and Right Arrow to move among tabs, then
        press Enter or Spacebar to activate the selected tab.
      </p>

      <p className="mb-1" style={{ fontWeight: 600 }}>
        Tables and Grids
      </p>
      <p className="mb-2">
        In questions that use tables, use table navigation keys to move between
        rows and columns. JAWS announces the row and column headings to help
        you stay oriented.
      </p>

      <p className="mb-0">
        Select <b>Return</b> to go back to the test screen.
      </p>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "question":
        return renderQuestionDirections();
      case "section":
        return renderSectionDirections();
      case "general":
        return renderGeneralDirections();
      case "tools":
        return renderTestingTools();
      case "screen":
        return renderScreenReaderHelp();
      default:
        return null;
    }
  };

  return (
    <div
      dir="ltr"
      className="d-flex justify-content-center"
      style={{ backgroundColor: "#ffffff", minHeight: "calc(100vh - 80px)" }}
    >
      <div
        className="bg-white"
        style={{
          maxWidth: "980px",
          width: "100%",
          marginTop: "16px",
          marginBottom: "16px",
          padding: "8px 40px 32px 40px",
          borderRadius: 0,
          textAlign: "left",
        }}
      >
        <div
          className="mb-3 pb-2"
          style={{
            borderBottom: "1px solid #dfcfd0",
            backgroundColor: "#f7ecee",
            marginLeft: "-40px",
            marginRight: "-40px",
            padding: "6px 40px 0 40px",
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  fontSize: "12px",
                  padding: "4px 12px",
                  marginRight: "6px",
                  marginBottom: "6px",
                  borderRadius: 3,
                  border: "1px solid #888",
                  backgroundColor: isActive ? "#d2d2d2" : "#e6e6e6",
                  boxShadow: isActive
                    ? "inset 0 0 0 1px #999"
                    : "0 0 0 1px #f0f0f0",
                  cursor: "pointer",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {renderContent()}
      </div>
    </div>
  );
}

export default Help;
